import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Badge, Button, Col, Input, Row } from 'reactstrap';
import AlertOutlineIcon from 'mdi-react/AlertOutlineIcon';
import LockOutlineIcon from 'mdi-react/LockOutlineIcon';
import Api from '../../../util/api';
import Loading from '../../../shared/components/custom/Loading';
import FormCard from '../../../shared/components/custom/FormCard';
import InfoBox from '../../../shared/components/custom/InfoBox';
import { partial } from '../../../util/translation/translation';
import {
  FEE_FIXED_MAX_KR,
  FEE_PERCENT_MAX,
  fetchClubNames,
  formatFee,
  hasMaxTwoDecimals,
  krToOre,
  oreToKr,
  parseDecimal,
} from '../../transactions/connect';

const isEmpty = (value) => `${value ?? ''}`.trim() === '';
const sameCurrency = (a, b) =>
  `${a || ''}`.toUpperCase() === `${b || ''}`.toUpperCase();

// Where the plan's payments go through Stripe Connect (nothing for clubs
// without it), with a warning when the receiver isn't ready. Super-users can
// give the plan its own receiver (locked once set) and override the fee.
// Saved on its own, not with the plan; it sits inside the plan's form, so
// plain inputs and type="button" (as PlanMilestonesPage).
const PlanConnectCard = ({ paymentOption }) => {
  const p = partial('connect');
  const isSuperAdmin = useSelector((state) =>
    state.admin.me?.permissions?.includes('super-admin')
  );
  const [connectInfo, setConnectInfo] = useState(null);
  const [receivers, setReceivers] = useState([]);
  const [clubNames, setClubNames] = useState({});
  const [receiverId, setReceiverId] = useState('');
  const [feePercent, setFeePercent] = useState('');
  const [feeFixedKr, setFeeFixedKr] = useState('');
  const [loading, setLoading] = useState(false);

  const applyOverride = (option) => {
    setFeePercent(
      option?.feePercent === null || option?.feePercent === undefined
        ? ''
        : `${option.feePercent}`
    );
    setFeeFixedKr(oreToKr(option?.feeFixedAmount));
  };

  const describe = async () => {
    try {
      const info = await Api.connect.describePaymentOption(paymentOption.id);
      setConnectInfo(info);
      setReceiverId(info.locked ? info.receiver?.id || '' : '');
    } catch {
      // Without the info there's nothing to show; the plan works as before
      setConnectInfo(null);
    }
  };

  useEffect(() => {
    if (!paymentOption?.id) {
      return;
    }
    describe();
    applyOverride(paymentOption);
    if (isSuperAdmin) {
      Api.connect
        .getAllReceivers()
        .then(setReceivers)
        .catch(() => setReceivers([]));
      fetchClubNames().then(setClubNames);
    }
    // describe is redefined every render
    // eslint-disable-next-line
  }, [paymentOption?.id, isSuperAdmin]);

  if (!connectInfo || (!connectInfo.clubUsesConnect && !isSuperAdmin)) {
    return null;
  }

  const { receiver, source, fee, ready, locked, clubUsesConnect } = connectInfo;
  const receiverOptions = receivers.filter(
    (item) =>
      sameCurrency(item.currency, paymentOption.currency) &&
      (!item.disconnectedAt || item.id === receiverId)
  );
  const clubLabel = (teamId) => clubNames[`${teamId}`] || `#${teamId}`;

  const feeValid =
    (isEmpty(feePercent) ||
      (hasMaxTwoDecimals(feePercent) &&
        parseDecimal(feePercent) <= FEE_PERCENT_MAX)) &&
    (isEmpty(feeFixedKr) ||
      (hasMaxTwoDecimals(feeFixedKr) &&
        parseDecimal(feeFixedKr) <= FEE_FIXED_MAX_KR));

  const save = async () => {
    setLoading(true);
    try {
      const option = await Api.connect.setPaymentOptionConnect(
        paymentOption.id,
        {
          ...(!locked && receiverId && { receiverId }),
          feePercent: isEmpty(feePercent) ? null : parseDecimal(feePercent),
          feeFixedAmount: isEmpty(feeFixedKr) ? null : krToOre(feeFixedKr),
        }
      );
      applyOverride(option);
      await describe();
      toast.success(p('planConnectSaved'));
    } catch (err) {
      toast.error(
        typeof err === 'string' && err ? err : p('planConnectSaveERROR')
      );
    } finally {
      setLoading(false);
    }
  };

  // A plan's own (locked) receiver that isn't ready refuses sign-ups; with
  // only the club's default, sponsors go on today's flow until it's ready
  const warning =
    clubUsesConnect && !ready
      ? p(locked ? 'planNotReadyLocked' : 'planNotReadyClub')
      : null;

  return (
    <Row className="mb-4">
      <Col md={12}>
        <FormCard title={p('planPayouts')}>
          <Loading loading={loading} />
          {clubUsesConnect && (
            <dl className="settings-form__facts mb-3">
              <div>
                <dt>{p('paymentsGoTo')}</dt>
                <dd>
                  {receiver ? (
                    <>
                      {receiver.name}{' '}
                      <span className="text-muted">
                        (
                        {source === 'option'
                          ? p('sourceOption')
                          : p('sourceClub')}
                        )
                      </span>
                    </>
                  ) : (
                    p('noReceiverTodaysFlow')
                  )}
                </dd>
              </div>
              {receiver && (
                <div>
                  <dt>{p('status')}</dt>
                  <dd>
                    <Badge color={ready ? 'success' : 'warning'}>
                      {ready ? p('status-ready') : p('notReady')}
                    </Badge>
                  </dd>
                </div>
              )}
              {receiver && (
                <div>
                  <dt>{p('fee')}</dt>
                  <dd>
                    {(fee && formatFee(fee.percent, fee.fixedAmount)) ||
                      p('feeNotSet')}
                  </dd>
                </div>
              )}
            </dl>
          )}
          {warning && (
            <InfoBox
              icon={<AlertOutlineIcon size={18} />}
              className={isSuperAdmin ? '' : 'mb-0'}
            >
              <strong>{warning}</strong>
            </InfoBox>
          )}
          {isSuperAdmin && (
            <>
              {clubUsesConnect && <hr />}
              <Row>
                <Col md={6}>
                  <div className="form__form-group">
                    <span className="form-header">{p('planReceiver')}</span>
                    <Input
                      type="select"
                      value={receiverId}
                      disabled={locked}
                      onChange={(e) => setReceiverId(e.target.value)}
                    >
                      <option value="">{p('planReceiverClubDefault')}</option>
                      {receiverOptions.map((item) => (
                        <option key={item.id} value={item.id}>
                          {`${item.name} · ${clubLabel(item.teamId)}${
                            item.isClubDefault ? ` (${p('clubDefault')})` : ''
                          }`}
                        </option>
                      ))}
                      {/* The locked receiver, even if it isn't listed */}
                      {locked &&
                        receiver &&
                        !receiverOptions.some(
                          (item) => item.id === receiver.id
                        ) && (
                          <option value={receiver.id}>{receiver.name}</option>
                        )}
                    </Input>
                    <span className="settings-form__help">
                      {locked ? (
                        <>
                          <LockOutlineIcon size={14} />{' '}
                          {p('planReceiverLocked')}
                        </>
                      ) : (
                        p('planReceiverHelp', {
                          currency: paymentOption.currency,
                        })
                      )}
                    </span>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="form__form-group">
                    <span className="form-header">{p('feePercent')}</span>
                    <div className="form__form-group-field">
                      <Input
                        value={feePercent}
                        inputMode="decimal"
                        placeholder={p('receiversFee')}
                        onChange={(e) => setFeePercent(e.target.value)}
                      />
                      <div className="form__form-group-icon">&nbsp;%&nbsp;</div>
                    </div>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="form__form-group">
                    <span className="form-header">{p('feeFixed')}</span>
                    <div className="form__form-group-field">
                      <Input
                        value={feeFixedKr}
                        inputMode="decimal"
                        placeholder={p('receiversFee')}
                        onChange={(e) => setFeeFixedKr(e.target.value)}
                      />
                      <div className="form__form-group-icon">
                        &nbsp;{p('kr')}&nbsp;
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>
              <span className="settings-form__help mt-0 mb-3">
                {feeValid
                  ? p('feeOverrideHelp')
                  : p('feeOverrideInvalid', {
                      percent: FEE_PERCENT_MAX,
                      fixed: FEE_FIXED_MAX_KR,
                    })}
              </span>
              <Button
                type="button"
                color="primary"
                size="sm"
                className="mb-0"
                disabled={!feeValid || loading}
                onClick={save}
              >
                {p('savePlanConnect')}
              </Button>
            </>
          )}
        </FormCard>
      </Col>
    </Row>
  );
};

PlanConnectCard.propTypes = {
  paymentOption: PropTypes.shape({
    id: PropTypes.string.isRequired,
    currency: PropTypes.string,
    feePercent: PropTypes.number,
    feeFixedAmount: PropTypes.number,
  }),
};

PlanConnectCard.defaultProps = {
  paymentOption: null,
};

export default PlanConnectCard;
